/**
 * `/gallery/score-rows` exists so a browser can measure the score row's
 * anatomy (ISSUE-908, Scores UX spec v1.0 section 1) and screenshot it at
 * 375, 390 and 1440, light and dark. It is signed in and linked from no
 * navigation, following the `/gallery/marks` precedent (ISSUE-541).
 *
 * A SEPARATE page from `/gallery/marks` on purpose: that one is the mark
 * plate's page, and `teamMarkGeometry.e2e.ts` asserts the exact rows it
 * renders.
 *
 * The file is named `gallery_.score-rows` so this route stays out of
 * `/gallery`'s tree: `/gallery` renders no Outlet, so a nested route would
 * never paint.
 *
 * Every string below is a real product value, not a stress string: the
 * longest names in the provider fixture corpus, the NFL pairs the owner's
 * 2026-10-19 screenshots show, and every branch of
 * `services/events-api/src/lib/statusCopy.ts` at its widest. The rows cover
 * every status, a favorite, possession, a tie, a team the server sends a
 * `shortName` for, and two ranked college teams with the poll rank badge,
 * one and two digits (ISSUE-1388).
 *
 * ONE page gutter. The page sits in AppShell's `<main>`, which already carries
 * `px-page-gutter`, so this screen adds none of its own: a score list sits
 * inside exactly one 16px gutter, the figure `scoreRowGeometry.ts` derives the
 * name cell from (124px at 375). The rows are wrapped in the same section card
 * `LeagueSectionCard` and the league view use, and pass `onOpenGame` so the
 * full row button they ship with is present.
 */
import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import { ScoreRow } from "../features/scores/ScoreRow";
import type { typeScoresEventInput } from "../features/scores/scoreRowModel";
import { ScoresSectionCard } from "../features/scores/ScoresSectionCard";
import { ScoresSectionLoading } from "../features/scores/ScoresSectionStates";

/** The longest team name in the whole provider fixture corpus, 27 characters,
 *  from tests/fixtures/provider/goalserve/football/div23/schedule/12-records.json. */
const LONGEST_CATALOG_TEAM_NAME = "Florida Memorial University";

/** The longest inside the four leagues ISSUE-908 D1 names, 24 characters,
 *  from tests/fixtures/provider/goalserve/football/fbs/schedule/10-records.json. */
const LONGEST_FBS_TEAM_NAME = "Southeast Missouri State";

/** The widest string `formatKickoff` can build: a three letter weekday, a two
 *  digit hour, and a day period. Measured at 95.5px in Inter 600 13.5px. */
const WIDEST_KICKOFF_LABEL = "Wed 10:44 AM";

/** The widest closed-set pairing of `derivePeriodLabel` and `deriveClockLabel`
 *  through `statusCopy.ts`. 101.43px in Inter 600 13.5px, rounded up to 102 in
 *  `tokens.test.ts`. "Inn 29 · 123:45" is 96.08px and is no longer the max. */
const WIDEST_LIVE_LABEL = "Inn 24 · 444:44";

const SCORE_ROW_EVENTS: readonly typeScoresEventInput[] = [
  {
    // The longest two names in the catalog, in one live row.
    eventId: "row-longest-live",
    status: "inProgress",
    statusLabel: "In progress",
    isLive: true,
    winnerTeamId: null,
    isTie: false,
    away: {
      teamId: "t-fmu",
      name: LONGEST_CATALOG_TEAM_NAME,
      abbreviation: "FMU",
      score: 102,
    },
    home: {
      teamId: "t-semo",
      name: LONGEST_FBS_TEAM_NAME,
      abbreviation: "SEMO",
      score: 98,
    },
  },
  {
    // The card's own R1 case, at the widest kickoff the formatter can build.
    eventId: "row-widest-kickoff",
    status: "scheduled",
    statusLabel: WIDEST_KICKOFF_LABEL,
    isLive: false,
    winnerTeamId: null,
    isTie: false,
    away: {
      teamId: "t-phi",
      name: "Philadelphia Phillies",
      abbreviation: "PHI",
      score: null,
    },
    home: {
      teamId: "t-min",
      name: "Minnesota Twins",
      abbreviation: "MIN",
      score: null,
    },
  },
  {
    // The widest status the product can emit, beside a long name.
    eventId: "row-widest-status",
    status: "inProgress",
    statusLabel: WIDEST_LIVE_LABEL,
    isLive: true,
    winnerTeamId: null,
    isTie: false,
    away: {
      teamId: "t-wsu",
      name: "Washington State",
      abbreviation: "WSU",
      score: 3,
    },
    home: {
      teamId: "t-laa",
      name: "Los Angeles Angels",
      abbreviation: "LAA",
      score: 2,
    },
  },
  {
    // ISSUE-908 D3: a WNBA final carries two numbers.
    eventId: "row-wnba-final",
    status: "final",
    statusLabel: "Final",
    isLive: false,
    winnerTeamId: "t-lynx",
    isTie: false,
    away: {
      teamId: "t-gsv",
      name: "Golden State Valkyries",
      abbreviation: "GSV",
      score: 71,
    },
    home: {
      teamId: "t-lynx",
      name: "Minnesota Lynx",
      abbreviation: "MIN",
      score: 84,
    },
  },
  {
    // The widest fixed label, beside the shortest names the catalog carries.
    eventId: "row-shortest-names",
    status: "postponed",
    statusLabel: "Postponed",
    isLive: false,
    winnerTeamId: null,
    isTie: false,
    away: { teamId: "t-kc", name: "KC", abbreviation: "KC", score: null },
    home: { teamId: "t-buf", name: "Bills", abbreviation: "BUF", score: null },
  },
  {
    // The pair ISSUE-908 was REPORTED on ("Washington C..." over
    // "Philadelphia P..."). The row now picks a label that fits instead of
    // cutting one; `pickTeamLabel` and `scoreRowGeometry.ts` decide which.
    eventId: "row-nfl-reported",
    status: "final",
    statusLabel: "Final",
    isLive: false,
    winnerTeamId: "t-wsh",
    isTie: false,
    away: {
      teamId: "t-wsh",
      name: "Washington Commanders",
      abbreviation: "WSH",
      score: 23,
    },
    home: {
      teamId: "t-phe",
      name: "Philadelphia Eagles",
      abbreviation: "PHI",
      score: 19,
    },
  },
  {
    eventId: "row-final-ot",
    status: "final",
    statusLabel: "Final/OT",
    isLive: false,
    winnerTeamId: "t-com",
    isTie: false,
    away: {
      teamId: "t-com",
      name: "Commanders",
      abbreviation: "WSH",
      score: 27,
    },
    home: { teamId: "t-bal", name: "Ravens", abbreviation: "BAL", score: 24 },
  },
  {
    // The owner's screenshot pair, with the server's nickname: at 375 the
    // full names do not fit and the row shows "Seahawks" and "49ers", never
    // "Seattle Se...". The home team has the ball.
    eventId: "row-short-name-live",
    status: "inProgress",
    statusLabel: "Q3 · 08:41",
    isLive: true,
    winnerTeamId: null,
    isTie: false,
    away: {
      teamId: "t-sea",
      name: "Seattle Seahawks",
      shortName: "Seahawks",
      abbreviation: "SEA",
      score: 10,
    },
    home: {
      teamId: "t-sf",
      name: "San Francisco 49ers",
      shortName: "49ers",
      abbreviation: "SF",
      score: 14,
    },
    liveState: {
      possessionTeamId: "t-sf",
      football: { ballSpotLabel: "SF 34", ballSpotPercentFromAway: 66 },
      downAndDistanceLabel: "3rd & 7",
      summaryLabel: "San Francisco 49ers have the ball at their own 34",
    },
  },
  {
    // A followed team: the inline star after its name, never on the mark.
    eventId: "row-favorite-scheduled",
    status: "scheduled",
    statusLabel: "Tue 11:00 PM",
    isLive: false,
    winnerTeamId: null,
    isTie: false,
    away: {
      teamId: "t-kc",
      name: "Kansas City Chiefs",
      shortName: "Chiefs",
      abbreviation: "KC",
      score: null,
    },
    home: {
      teamId: "t-buf-fav",
      name: "Buffalo Bills",
      shortName: "Bills",
      abbreviation: "BUF",
      score: null,
    },
  },
  {
    // A tie: neither side is muted.
    eventId: "row-tie",
    status: "final",
    statusLabel: "Final/OT",
    isLive: false,
    winnerTeamId: null,
    isTie: true,
    away: {
      teamId: "t-ari",
      name: "Arizona Cardinals",
      abbreviation: "ARI",
      score: 20,
    },
    home: {
      teamId: "t-lar",
      name: "Los Angeles Rams",
      abbreviation: "LAR",
      score: 20,
    },
  },
  {
    eventId: "row-delayed",
    status: "delayed",
    statusLabel: "Delayed",
    isLive: true,
    winnerTeamId: null,
    isTie: false,
    away: {
      teamId: "t-nyy",
      name: "New York Yankees",
      abbreviation: "NYY",
      score: 1,
    },
    home: {
      teamId: "t-bos",
      name: "Boston Red Sox",
      abbreviation: "BOS",
      score: 0,
    },
  },
  {
    // ISSUE-1381. A scheduled MLB game with probable starters: one with his
    // season record and ERA, the other not announced yet.
    eventId: "row-mlb-starters",
    status: "scheduled",
    statusLabel: "Wed 7:10 PM",
    isLive: false,
    winnerTeamId: null,
    isTie: false,
    away: {
      teamId: "t-lad",
      name: "Los Angeles Dodgers",
      abbreviation: "LAD",
      score: null,
    },
    home: {
      teamId: "t-mil",
      name: "Milwaukee Brewers",
      abbreviation: "MIL",
      score: null,
    },
    startingPitchers: {
      away: null,
      home: { name: "Gasser", record: "12-8", era: "5.19" },
    },
  },
  {
    // ISSUE-1388: an FBS final between two ranked teams, a one digit and a
    // two digit poll rank before the names.
    eventId: "row-ranked-fbs",
    status: "final",
    statusLabel: "Final",
    isLive: false,
    winnerTeamId: "t-mia-fbs",
    isTie: false,
    away: {
      teamId: "t-ttu-fbs",
      name: "Texas Tech Red Raiders",
      shortName: "Texas Tech",
      abbreviation: "TTU",
      score: 17,
      pollRankLabel: "25",
    },
    home: {
      teamId: "t-mia-fbs",
      name: "Miami Hurricanes",
      shortName: "Miami",
      abbreviation: "MIA",
      score: 31,
      pollRankLabel: "5",
    },
  },
  {
    eventId: "row-canceled",
    status: "canceled",
    statusLabel: "Canceled",
    isLive: false,
    winnerTeamId: null,
    isTie: false,
    away: {
      teamId: "t-det",
      name: "Detroit Lions",
      abbreviation: "DET",
      score: null,
    },
    home: {
      teamId: "t-chi",
      name: "Chicago Bears",
      abbreviation: "CHI",
      score: null,
    },
  },
];

/** The gallery's followed team, so the favorite row shows its star. */
const GALLERY_FAVORITE_TEAM_IDS: readonly string[] = ["t-buf-fav"];

/** This page has nowhere to navigate to; what it needs from `onOpenGame` is
 *  the full row button a shipped row carries. */
const openNothing = (): void => undefined;

function RowCard(props: {
  title: string;
  rows: readonly typeScoresEventInput[];
}): ReactNode {
  return (
    <ScoresSectionCard testId="score-rows-card" title={props.title}>
      {props.rows.map((event) => (
        <ScoreRow
          key={event.eventId}
          event={event}
          onOpenGame={openNothing}
          favoriteTeamIds={GALLERY_FAVORITE_TEAM_IDS}
        />
      ))}
    </ScoresSectionCard>
  );
}

const ScoreRowsGalleryScreen = (): ReactNode => (
  <div className="flex flex-col gap-6 pt-4">
    <h1 className="text-h1 text-ink">Score row gallery</h1>
    <p className="text-body text-ink-secondary">
      Every status, the longest team names the catalog carries and the widest
      status string the product can emit, in the cards that have to hold them.
      This page exists so a browser can measure them. It is not linked from any
      navigation.
    </p>

    <div data-testid="score-rows-gallery" className="flex flex-col gap-3">
      <h2 className="text-h2 text-ink">Rows at the page width</h2>
      <RowCard title="NFL" rows={SCORE_ROW_EVENTS} />
    </div>

    <div
      data-testid="score-rows-gallery-narrow"
      className="flex flex-col gap-3"
    >
      <h2 className="text-h2 text-ink">Rows in the league view column</h2>
      {/* The league view's grid, class for class: the 320px calendar track
          beside the card at lg. */}
      <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-[var(--sm-size-scores-calendar-track)_minmax(0,1fr)] lg:gap-6">
        <div className="hidden lg:block">
          <p className="text-body text-ink-secondary">
            The calendar track. Empty here: only its width matters.
          </p>
        </div>
        <div data-testid="score-rows-narrow-column">
          <RowCard title="Monday, Oct 19" rows={SCORE_ROW_EVENTS} />
        </div>
      </div>
    </div>

    <div
      data-testid="score-rows-gallery-loading"
      className="flex flex-col gap-3"
    >
      <h2 className="text-h2 text-ink">Loading</h2>
      <ScoresSectionCard testId="score-rows-loading-card" title="NFL">
        <ScoresSectionLoading testId="score-rows-loading" />
      </ScoresSectionCard>
    </div>
  </div>
);

export const Route = createFileRoute("/gallery_/score-rows")({
  beforeLoad: requireSignedIn,
  component: ScoreRowsGalleryScreen,
});
